/**
 * The bridge between the derivation rules and the form.
 *
 * The rule a reader is most likely to want stated plainly: a derived field
 * keeps following its source until the editor types something else into it,
 * and then it stops for the rest of the session. Working that out needs a
 * memory of what the CMS last wrote — without one, the first keystroke in the
 * title would make the slug look edited, because the slug would no longer match
 * what the new title derives to. That memory is `expectedRef` below, and the
 * resulting "still following" set is shared through `derivationStore` so that
 * fields elsewhere in the form can label themselves honestly.
 */

import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useAuthController, type FormContext } from "@firecms/core";

import { canEdit, roleOf } from "../roles";
import {
    DERIVED_FIELD_BY_KEY,
    DERIVED_FIELDS,
    DerivableKey,
    deriveValues,
    GuideValues,
    isEmptyValue,
    sameValue,
    trackingFields
} from "../derive/autofill";
import { DEFAULT_SITE_ORIGIN } from "../derive/text";
import {
    getTracking,
    resetTracking,
    resumeTracking,
    stopTracking,
    subscribeTracking
} from "./derivationStore";

export const SITE_ORIGIN = import.meta.env.VITE_SITE_ORIGIN || DEFAULT_SITE_ORIGIN;

/* Long enough that a body edit does not re-rank keywords on every keystroke,
   short enough that the slug appears to follow the title as it is typed. */
const SETTLE_MS = 250;

/** One entry per open form. A new guide has no id yet, and needs no more. */
export const formKeyOf = (context: FormContext<any>): string =>
    `${context.path ?? ""}:${context.entityId || "new"}`;

/** Re-renders the caller whenever a field starts or stops following its source. */
export function useTracking(formKey: string): ReadonlySet<DerivableKey> {
    const subscribe = useCallback(
        (listener: () => void) => subscribeTracking(formKey, listener),
        [formKey]
    );
    return useSyncExternalStore(subscribe, () => getTracking(formKey));
}

/**
 * Keeps every derivable field in step with what the editor has written.
 *
 * Mounted once, from the title field, because the title is the one field that
 * is always on screen. A per-field effect would only run for fields that happen
 * to be mounted, and the fields most worth deriving are exactly the ones
 * sitting in a collapsed panel nobody opens. `fillEmptyDerivable` in the
 * collection's `onPreSave` covers the case where even the title is not
 * rendered.
 */
export function useAutofill(context: FormContext<any>): void {
    const values = context.values as GuideValues;
    const setFieldValue = context.setFieldValue;
    const disabled = context.disabled;
    const formKey = formKeyOf(context);

    /* `firestore.rules` lets a reviewer change only status, reviewerId,
       lastReviewedAt and updatedAt. Filling in a missing SEO title on their
       behalf would put a fourth key in the diff and get their approval refused,
       so derivation belongs to the roles that write content. */
    const authController = useAuthController();
    const mayFill = canEdit(roleOf(authController));

    /* What this hook last put in each field. The difference between that and
       what is in the form now is the editor's own typing, and the only reliable
       signal that a field should stop following. */
    const expectedRef = useRef(new Map<DerivableKey, unknown>());
    const openedRef = useRef<string | undefined>(undefined);

    /* Seeding during render rather than in an effect: sibling fields read this
       set on their very first paint, and an effect runs too late for that, so
       they would all flash "Yours" on open. The write is idempotent and guarded
       by the ref, which is what makes it safe to repeat. */
    if (openedRef.current !== formKey) {
        openedRef.current = formKey;
        expectedRef.current = new Map();
        resetTracking(formKey, trackingFields(values, SITE_ORIGIN));
    }

    useEffect(() => {
        if (disabled || !mayFill) return undefined;

        const timer = setTimeout(() => {
            const following = new Set(getTracking(formKey));

            /* What each field would derive to under the current membership,
               needed before membership can be decided: a value that already
               equals its derivation is not a disagreement. */
            const { resolved: candidate } = deriveValues(values, following, SITE_ORIGIN);

            for (const { key, lockedBecause } of DERIVED_FIELDS) {
                const current = values[key];
                const expected = expectedRef.current.get(key);

                if (following.has(key)) {
                    /* Typed over: stop, and stay stopped. Agreeing with the
                       derivation is not typing over it, which is what lets
                       "use the derived value" hand the field back rather than
                       leaving it permanently marked as an override. */
                    const editedAway = expected !== undefined
                        && !isEmptyValue(current)
                        && !sameValue(current, expected)
                        && !sameValue(current, candidate[key]);
                    if (editedAway) {
                        following.delete(key);
                        stopTracking(formKey, key);
                    }
                } else if (isEmptyValue(current) && !lockedBecause?.(values)) {
                    /* Emptied: clearing a field is how you ask for the automatic
                       value back, and it is more discoverable than any button. */
                    following.add(key);
                    resumeTracking(formKey, key);
                }
            }

            const { changes, resolved } = deriveValues(values, following, SITE_ORIGIN);

            for (const key of following) {
                const next = resolved[key] ?? values[key];
                if (next !== undefined) expectedRef.current.set(key, next);
            }

            for (const [key, value] of Object.entries(changes)) {
                /* Validate on write so a "required" error on a field the CMS
                   just filled in clears itself instead of waiting for a save. */
                setFieldValue(key, value, true);
            }
        }, SETTLE_MS);

        return () => clearTimeout(timer);
    }, [values, setFieldValue, disabled, mayFill, formKey]);
}

export interface DerivedState {
    /** What the rules say this field should be, override or not. */
    derived: unknown;
    /** True while the field is still following its source. */
    tracking: boolean;
    /** Why the CMS has stopped updating this field, if it has. */
    locked?: string;
    label: string;
    from: string;
    /** Puts the field back under automatic control. */
    resume: () => void;
}

/** The derivation status of one field, for the UI to explain itself with. */
export function useDerivedState(key: DerivableKey, context: FormContext<any>): DerivedState {
    const values = context.values as GuideValues;
    const formKey = formKeyOf(context);
    const following = useTracking(formKey);
    const resume = useCallback(() => resumeTracking(formKey, key), [formKey, key]);

    return useMemo(() => {
        const spec = DERIVED_FIELD_BY_KEY[key];
        const { resolved } = deriveValues(values, new Set(following), SITE_ORIGIN);
        const locked = spec.lockedBecause?.(values);

        return {
            derived: resolved[key],
            tracking: !locked && following.has(key),
            ...(locked ? { locked } : {}),
            label: spec.label,
            from: spec.from,
            resume
        };
    }, [key, values, following, resume]);
}
